import React, { createContext, useContext, ReactNode } from 'react';
import { useLandingSections, type LandingSection } from '@/hooks/useLandingSections';

interface SectionsContextType {
  sections: LandingSection[];
  isLoading: boolean;
  getSectionContent: <T>(slug: string) => T | null;
  isSectionVisible: (slug: string) => boolean;
}

const SectionsContext = createContext<SectionsContextType | null>(null);

export function useSectionsContext() {
  const context = useContext(SectionsContext);
  if (!context) {
    throw new Error('useSectionsContext must be used within SectionsProvider');
  }
  return context;
}

export function SectionsProvider({ children }: { children: ReactNode }) {
  const { sections, isLoading, getSectionContent, isSectionVisible } = useLandingSections(false);

  return (
    <SectionsContext.Provider value={{ sections, isLoading, getSectionContent, isSectionVisible }}>
      {children}
    </SectionsContext.Provider>
  );
}

// Higher-order component to conditionally render sections
export function DynamicSection({ 
  slug, 
  fallback, 
  dynamic 
}: { 
  slug: string; 
  fallback: ReactNode; 
  dynamic: (content: Record<string, unknown>) => ReactNode;
}) {
  const { getSectionContent, isSectionVisible, isLoading } = useSectionsContext();

  // During loading, show the fallback
  if (isLoading) {
    return <>{fallback}</>;
  }

  // Check if section should be visible
  if (!isSectionVisible(slug)) {
    return null;
  }

  const content = getSectionContent<Record<string, unknown>>(slug);
  
  // If we have content from DB, use dynamic component
  if (content && Object.keys(content).length > 0) {
    return <>{dynamic(content)}</>;
  }

  // Fallback to static content
  return <>{fallback}</>;
}
